import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Video, MessagesSquare, TrendingUp } from "lucide-react";
import Seo from "../components/Seo";
import Marquee from "../components/Marquee";
import ServiceBento from "../components/ServiceBento";
import CaptionImage from "../components/CaptionImage";
import { Reveal, MaskedLines, Chapter } from "../components/Motion";
import { IMAGES } from "../site/content";

const GLOBAL_CARDS = [
  {
    lead: "Translation",
    text: "Our team delivers precise, culturally appropriate translations across major languages. We handle documents, websites, marketing materials, subtitles, and technical content so your message stays clear and professional in every market.",
    img: IMAGES.globalTranslation,
  },
  {
    lead: "Media content",
    text: "We produce engaging videos, social reels, promotional films, motion graphics, and branded stories that capture attention and communicate your vision. Every project is built to reflect your brand and connect with the people you need to reach.",
    img: IMAGES.mediaStory,
  },
  {
    lead: "Digital advertising",
    text: "We design and manage targeted campaigns on Google, Meta, and YouTube. Smart strategy, creative assets, and ongoing optimisation to generate leads, lift conversions, and improve return on investment.",
    img: IMAGES.digitalCampaign,
  },
];

const FLEX_POINTS = [
  "On-demand video lessons so learners study at the hour that works for them",
  "Live 1:1 tuition for questions, practice, and feedback",
  "Step-by-step modules that can be paused, replayed, and resumed",
  "School-year and exam-paced Maths tracks; beginner-to-enthusiast Cooking tracks; age-open Art tracks (drawing, painting, digital)",
];

const FLEX_MODEL = [
  {
    icon: Video,
    step: "01",
    title: "Watch when you want",
    text: "On-demand video lessons fit the hour that works for the learner — early morning, late night, or between shifts.",
  },
  {
    icon: MessagesSquare,
    step: "02",
    title: "Practise with a tutor",
    text: "Live 1:1 sessions for questions, practice, and honest feedback from a real teacher.",
  },
  {
    icon: TrendingUp,
    step: "03",
    title: "Progress at your pace",
    text: "Modules pause, replay, and resume. No cohort deadlines — progress is measured in skill, not speed.",
  },
];

const LEARN_CARDS = [
  {
    title: "Mathematics",
    text: "Concept-first lessons and personal support for school and competitive exams.",
    img: IMAGES.mathClass,
  },
  {
    title: "Cooking",
    text: "Practical video guidance for beginners and enthusiasts.",
    img: IMAGES.cookingClass,
  },
  {
    title: "Art",
    text: "Structured tutorials plus personalised tuition — drawing, painting, and digital.",
    img: IMAGES.artClass,
  },
];

const WHY = [
  { label: "Specialised expertise", text: "Practitioners in language, media, advertising, and teaching — not a generic agency." },
  { label: "Modern tools", text: "Clean delivery, current platforms, and production quality that holds up in market." },
  { label: "Fast turnaround", text: "Speed without sloppy work." },
  { label: "Personal attention", text: "Every project has an owner. Materials are handled securely." },
  { label: "Measurable results", text: "Clearer communication, stronger online presence, or real skill gain." },
  { label: "Marketing that works", text: "Creative and media are built to be found, not just to look finished." },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const blobY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-28 sm:pt-36 pb-16 sm:pb-24" data-testid="home-hero">
      <motion.div
        style={{ y: blobY }}
        aria-hidden="true"
        className="absolute -right-32 top-24 -z-0 h-[420px] w-[420px] rounded-full bg-sand blur-3xl opacity-80"
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="eyebrow flex items-center gap-3"
              data-testid="hero-eyebrow"
            >
              <span className="h-px w-8 bg-teal" aria-hidden="true" />
              Professional digital solutions and transformative learning
            </motion.p>
            <MaskedLines
              lines={[
                "Welcome to",
                <span key="p" className="italic text-teal">ProOne.</span>,
              ]}
              className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] text-charcoal"
            />
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-charcoal-muted"
              data-testid="hero-subhead"
            >
              High-quality transcription, accurate translation, compelling media,
              result-driven digital advertising, and expert video training with
              personalised tuition in Mathematics, Cooking, and Art.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.75 }}
              className="mt-9 flex flex-wrap items-center gap-3"
            >
              <Link to="/services" className="btn-primary" data-testid="hero-explore-services-btn">
                Explore Services <ArrowRight size={16} aria-hidden="true" />
              </Link>
              <Link to="/learn" className="btn-outline" data-testid="hero-start-learning-btn">
                Start Learning
              </Link>
              <Link
                to="/our-work"
                className="btn px-2 text-teal underline decoration-teal/40 underline-offset-8 hover:decoration-teal"
                data-testid="hero-support-rural-btn"
              >
                Support Rural India
              </Link>
            </motion.div>
          </div>
          <div className="lg:col-span-5">
            <motion.div
              style={{ y: imgY }}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="will-change-transform"
            >
              <CaptionImage image={IMAGES.heroStudio} ratio="aspect-[4/5]" rounded="rounded-[2rem]" priority />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo
        title="Transcription, Translation, Media, Advertising & Learning"
        description="High-quality transcription, accurate translation, compelling media, result-driven digital advertising, and personalised tuition in Maths, Cooking, and Art — plus a registered nonprofit serving rural India."
        path="/"
      />
      <Hero />

      <section className="py-16 sm:py-24" data-testid="key-services-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Chapter number="01" eyebrow="Key services" title="Five things we do exceptionally well — pick one and start." />
          <ServiceBento />
        </div>
      </section>

      <Marquee />

      <section className="py-16 sm:py-24" data-testid="global-reach-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Chapter number="02" eyebrow="Global reach" title="Need to reach global audiences?" />
          <div className="space-y-8">
            {GLOBAL_CARDS.map((c, i) => (
              <Reveal key={c.lead} delay={0.05}>
                <div
                  className={`grid items-stretch gap-0 overflow-hidden rounded-3xl border border-line bg-white/60 md:grid-cols-2 ${
                    i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
                  }`}
                  data-testid={`global-card-${c.lead.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="min-h-[240px] md:min-h-[300px]">
                    <img
                      src={c.img.url}
                      alt={c.img.alt}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="flex flex-col justify-center p-7 sm:p-10">
                    <p className="text-base sm:text-lg leading-relaxed text-charcoal">
                      <strong className="font-serif text-xl sm:text-2xl text-teal">{c.lead}</strong>
                      <span className="mx-2 text-charcoal/40">—</span>
                      {c.text}
                    </p>
                    <p className="caption-text mt-6 flex items-start gap-3">
                      <span className="mt-[7px] h-px w-6 shrink-0 bg-current" aria-hidden="true" />
                      {c.img.caption}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-sand/60 border-y border-line py-16 sm:py-24" data-testid="learning-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Chapter number="03" eyebrow="Learning & education" title="Beyond business solutions — learning that fits a real life." />
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <Reveal>
                <p className="text-base sm:text-lg leading-relaxed text-charcoal" data-testid="learning-intro">
                  <strong className="text-teal">Beyond business solutions, ProOne is passionate about learning and education.</strong>{" "}
                  Here is what flexible actually means:
                </p>
                <ul className="mt-7 space-y-4">
                  {FLEX_POINTS.map((p) => (
                    <li key={p} className="flex gap-3 text-sm sm:text-base leading-relaxed text-charcoal/90">
                      <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rotate-45 bg-teal" aria-hidden="true" />
                      {p}
                    </li>
                  ))}
                </ul>
              </Reveal>
              <div className="mt-10 grid gap-4 sm:grid-cols-3" data-testid="flexibility-model">
                {FLEX_MODEL.map((f, i) => (
                  <Reveal key={f.step} delay={i * 0.08}>
                    <div className="h-full rounded-2xl border border-line bg-cream p-5">
                      <f.icon size={22} className="text-teal" strokeWidth={1.7} aria-hidden="true" />
                      <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/50">{f.step}</p>
                      <p className="mt-1.5 font-serif text-lg font-semibold text-charcoal">{f.title}</p>
                      <p className="mt-2 text-xs leading-relaxed text-charcoal-muted">{f.text}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
            <div className="lg:col-span-6">
              <Reveal delay={0.1}>
                <CaptionImage image={IMAGES.learnTutor} ratio="aspect-[4/3] lg:aspect-[4/4.2]" rounded="rounded-[2rem]" />
              </Reveal>
            </div>
          </div>
          <div className="mt-14 grid gap-5 md:grid-cols-3" data-testid="learning-cards">
            {LEARN_CARDS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <Link
                  to="/learn"
                  data-testid={`home-learn-card-${c.title.toLowerCase()}`}
                  className="group block overflow-hidden rounded-3xl border border-line bg-cream transition-transform duration-500 hover:-translate-y-1.5"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={c.img.url}
                      alt={c.img.alt}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-2xl font-semibold text-charcoal">{c.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-charcoal-muted">{c.text}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-teal">
                      Explore <ArrowRight size={13} aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="why-choose-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Chapter number="04" eyebrow="Why ProOne" title="Why choose ProOne" />
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <ul className="grid gap-x-10 gap-y-7 sm:grid-cols-2" data-testid="why-choose-list">
                {WHY.map((w, i) => (
                  <Reveal key={w.label} delay={i * 0.05}>
                    <li className="text-sm sm:text-base leading-relaxed text-charcoal/90">
                      <strong className="mb-1 block font-serif text-lg text-teal">{w.label}</strong>
                      {w.text}
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <CaptionImage image={IMAGES.ownership} ratio="aspect-[4/3]" />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-20 sm:pb-28" data-testid="final-cta-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2.5rem] bg-teal-dark px-6 py-16 sm:px-14 sm:py-20 text-center">
              <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-teal/40 blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-28 -right-20 h-80 w-80 rounded-full bg-sand/10 blur-3xl" aria-hidden="true" />
              <p className="eyebrow !text-sand/80">One team, four doors</p>
              <h2 className="mx-auto mt-5 max-w-3xl font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold leading-snug text-cream" data-testid="final-cta-headline">
                Ready to elevate your content, your reach, and your learning?
              </h2>
              <p className="mx-auto mt-5 max-w-2xl text-sm sm:text-base leading-relaxed text-cream/75">
                Whether you need polished transcripts, translation for a new market, media that
                travels, advertising that converts, or a flexible course with a real tutor —
                ProOne is built for clarity, reach, and growth.
              </p>
              <div className="mt-9 flex flex-wrap justify-center gap-3">
                <Link to="/contact?intent=Hire" className="btn-light" data-testid="cta-consultation-btn">Book a free consultation</Link>
                <Link to="/services#brief" className="btn-ghost-light" data-testid="cta-brief-campaign-btn">Brief a campaign</Link>
                <Link to="/learn" className="btn-ghost-light" data-testid="cta-start-course-btn">Start a course</Link>
                <Link to="/support?tab=donate" className="btn-sand" data-testid="cta-support-rural-btn">Support our rural work</Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
